import EmptyFolder from 'assets/img/app/empty-folder.png';
import { IonContent, IonDatetime, IonIcon, IonPage, IonSpinner } from "@ionic/react"
import { SidebarMenu, SolidButton } from "components"
import { useEffect, useRef, useState } from "react"
import { formatDateToISODateString, formatDateToLocaleDateString, formatISODateStringToDate } from "utils";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, Pagination } from '@mui/material';
import { caretDown } from 'ionicons/icons';
import axios from 'axios';
import { useAppSelector } from 'hooks';

export const InventoryPage = () => {
    const { token } = useAppSelector(state => state.user);
    const today = new Date();
    const recordsPerView = 10;

    // estado para almacenar los benchmarks recuperados
    const [report, setReport] = useState<any[]>([]);
    const [backupReport, setBackupReports] = useState<any[]>([]);


    // estados para manejar las pantallas de carga
    const [loading, setLoading] = useState<boolean>(true);

    // estados para manejar la paginación
    const [paginationCount, setPaginationCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);


    // estado que almacena el rango de fechas de los benchmarks consultadas
    const [startDate, setStartDate] = useState<Date>(new Date((new Date()).setDate(today.getDate() - 15)));
    const [finishDate, setFinishDate] = useState<Date>(new Date());

    // estados para almacenar los valores que aparecerán en los filtros de búsqueda
    const [searchFilters, setSearchFilters] = useState<{ user: any, sucursal: any }>({ user: [], sucursal: [] });

    const contentRef = useRef<HTMLIonContentElement>(null);

    useEffect(() => {
        const controller = new AbortController();
        getReportData(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Función para recuperar la información a presentar en el reporte
    const getReportData = async (signal: AbortSignal) => {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/benchmark/get/report`, {
            params: {
                startDate: `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`,
                endDate: `${finishDate.getFullYear()}-${(finishDate.getMonth() + 1).toString().padStart(2, '0')}-${finishDate.getDate().toString().padStart(2, '0')}`,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `UserToken ${token}`
            },
            signal: signal
        })
        setLoading(false);

        const report = response.data?.data?.inventory ?? []
        setReport(report);
        setBackupReports(report);
        setPaginationCount(Math.ceil(report.length / recordsPerView));
        setCurrentPage(1);

        setSearchFilters(() => {
            let user = response.data?.data?.accounts || [];
            let sucursal = response.data?.data?.sucursals || [];
            user = user.map((account: any) => ({ ...account, checked: true }))
            sucursal = sucursal.map((suc: any) => ({ ...suc, checked: true }))
            return {
                sucursal: [{
                    key: 'all',
                    name: 'Todas las sucursales',
                    checked: true
                }, ...sucursal],
                user: [{
                    key: 'all',
                    name: 'Todos los displays',
                    lastName: '',
                    checked: true
                }, ...user]
            }
        })
    }

    const changePaginationHandler = (_: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    const changeSearchFilterHandler = (item: any, filterType: "sucursal" | "user") => {
        setSearchFilters((prevFilters) => {
            let updatedFilterItems: any[];

            if (item.key === 'all') {
                const newCheckedValue = !item.checked;
                updatedFilterItems = prevFilters[filterType].map((filterItem: any) => ({
                    ...filterItem,
                    checked: newCheckedValue,
                }));
            } else {
                updatedFilterItems = prevFilters[filterType].map((filterItem: any) => {
                    if (filterItem.key === item.key) {
                        return { ...filterItem, checked: !filterItem.checked };
                    }
                    return { ...filterItem };
                });
                updatedFilterItems = normalizeAllCheckbox(updatedFilterItems);
            }

            const nextFilters = { ...prevFilters, [filterType]: updatedFilterItems };
            const filteredReport = filterInventoryReport(nextFilters);

            setReport(filteredReport);
            setPaginationCount(Math.ceil(filteredReport.length / recordsPerView));
            setCurrentPage(1);

            return nextFilters;
        })
    }

    const normalizeAllCheckbox = (items: any[]) => {
        const allIndex = items.findIndex((filterItem: any) => filterItem.key === 'all');
        if (allIndex === -1) return items;
        const allChecked = items
            .filter((filterItem: any) => filterItem.key !== 'all')
            .every((filterItem: any) => filterItem.checked);
        return items.map((filterItem: any, idx: number) =>
            idx === allIndex ? { ...filterItem, checked: allChecked } : filterItem
        );
    };

    const getDisplayIdentifier = (display: any) =>
        String(display?.displayKey ?? display?.userId ?? display?.id ?? display?.key ?? display?.name ?? '');

    const getSucursalIdentifier = (sucursal: any) =>
        String(sucursal?.sucursalId ?? sucursal?.id ?? sucursal?.key ?? sucursal?.name ?? '');

    type FilterSelection = { mode: 'all' | 'some' | 'none'; keys: string[] };

    const computeSelection = (items: any[]): FilterSelection => {
        if (!Array.isArray(items) || items.length === 0) return { mode: 'all', keys: [] };
        const allOption = items.find((item: any) => item.key === 'all');
        if (allOption?.checked) return { mode: 'all', keys: [] };
        const keys = items
            .filter((item: any) => item.key !== 'all' && item.checked)
            .map((item: any) => String(item.key));
        if (keys.length === 0) return { mode: 'none', keys: [] };
        return { mode: 'some', keys };
    };

    const filterInventoryReport = (filtersState: { user: any[]; sucursal: any[] }) => {
        const userSelection = computeSelection(filtersState.user);
        const sucursalSelection = computeSelection(filtersState.sucursal);

        if (userSelection.mode === 'none' || sucursalSelection.mode === 'none') return [];

        return backupReport.reduce((acc: any[], record: any) => {
            const filteredDisplays = (record?.displays ?? []).reduce((displayAcc: any[], display: any) => {
                if (userSelection.mode === 'some') {
                    const displayKey = getDisplayIdentifier(display);
                    if (!userSelection.keys.includes(displayKey)) return displayAcc;
                }

                const filteredSucursals = (display?.sucursals ?? []).filter((sucursal: any) => {
                    if (sucursalSelection.mode === 'all') return true;
                    const sucursalKey = getSucursalIdentifier(sucursal);
                    return sucursalSelection.keys.includes(sucursalKey);
                });

                if (sucursalSelection.mode !== 'all' && filteredSucursals.length === 0) return displayAcc;

                displayAcc.push({ ...display, sucursals: filteredSucursals });
                return displayAcc;
            }, []);

            if (filteredDisplays.length > 0) acc.push({ ...record, displays: filteredDisplays });
            return acc;
        }, []);
    };

    return (
        <IonPage>
            <IonContent ref={contentRef}>
                <div className="w-full h-screen grid grid-cols-12">
                    {/* Menú lateral */}
                    <div className="col-span-2 border-r-2 border-solid border-light">
                        <SidebarMenu pageName="inventory" />
                    </div>

                    <div className="w-full h-screen flex flex-col flex-grow col-span-10">
                        <div className="w-full flex justify-between border-b-2 border-tertiary px-4 py-3">
                            <p className="text-primary font-bold text-lg">Inventario</p>

                            <p className="font-semibold text-lg">Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}</p>
                        </div>

                        <div
                            id="main-section"
                            className="w-full h-screen overflow-y-scroll grid grid-cols-12 gap-4"
                        >
                            {loading ? (
                                <div className="col-span-12 w-full flex flex-col items-center justify-center">
                                    <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
                                </div>
                            ) : (
                                <>
                                    {report.length === 0 ?
                                        <div className="col-span-9 w-full flex flex-col flex-grow">
                                            <div className="h-full grid grid-cols-8">
                                                <div className="col-span-8 flex flex-col items-center gap-4 p-4">
                                                    <img src={EmptyFolder} alt="empty" className="h-[200px] object-contain" />
                                                    <p className="text-xl text-center font-semibold text-primary">
                                                        No se encontraron resultados para tu búsqueda
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        :
                                        <div className="col-span-9 w-full h-full flex flex-col">
                                            {report.map((repo: any, index: number) => (
                                                index >= (recordsPerView * currentPage - recordsPerView) && index < (recordsPerView * currentPage) ?
                                                    <Accordion key={index} sx={{ boxShadow: "none;" }}>
                                                        <AccordionSummary
                                                            sx={{ display: "flex", flexDirection: "row-reverse", backgroundColor: "#F4F5F5" }}
                                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                                            aria-controls="panel1a-content"
                                                            id="panel1a-header"
                                                        >
                                                            <div className="w-full flex justify-between items-center pl-2 text-mid">
                                                                <p>{repo.date}</p>
                                                                <p className="font-bold">{repo.displays?.length}</p>
                                                            </div>
                                                        </AccordionSummary>

                                                        <AccordionDetails>
                                                            {repo.displays.map((display: any, indx: number) => (
                                                                <Accordion key={`${index}_${indx}`} sx={{ boxShadow: "none;" }}>
                                                                    <AccordionSummary
                                                                        sx={{ display: "flex", flexDirection: "row-reverse", backgroundColor: "#F4F5F5" }}
                                                                        expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                                                        aria-controls="panel1a-content"
                                                                        id="panel1a-header"
                                                                    >
                                                                        <div className="w-full flex justify-between items-center pl-2 text-mid">
                                                                            <p>{display.name}</p>
                                                                            <p className="font-bold">{display.sucursals?.length}</p>
                                                                        </div>
                                                                    </AccordionSummary>

                                                                    <AccordionDetails>
                                                                        {display.sucursals.map((sucursal: any, id: number) => (
                                                                            <Accordion key={`${index}_${indx}_${id}`} sx={{ boxShadow: "none;" }}>
                                                                                <AccordionSummary
                                                                                    sx={{ display: "flex", flexDirection: "row-reverse", backgroundColor: "#F4F5F5" }}
                                                                                    expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                                                                    aria-controls="panel1a-content"
                                                                                    id="panel1a-header"
                                                                                >
                                                                                    <div className="w-full flex justify-between items-center pl-2 text-mid">
                                                                                        <p>{sucursal.name}</p>
                                                                                        <p className="font-bold">{sucursal.created}</p>
                                                                                    </div>
                                                                                </AccordionSummary>

                                                                                <AccordionDetails>
                                                                                    {sucursal.products?.length > 0 ? (
                                                                                        sucursal.products?.map((product: any, i: number) => (
                                                                                            <div
                                                                                                key={`${index}_${indx}_${id}_${i}`}
                                                                                                className="flex justify-between border-b-2 last:border-b-0 border-tertiary"
                                                                                            >
                                                                                                <p>{product.name}</p>
                                                                                                <p>{product.quantity}</p>
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        <p>No hay registro de que se ingresara productos de Stock para esta sucursal.</p>
                                                                                    )}
                                                                                    {sucursal.notes?.notes && (
                                                                                        <div className="mt-2 p-2 border-2 border-dashed border-secondary rounded-lg bg-light">
                                                                                            <p className="font-semibold text-secondary mb-1">Notas:</p>
                                                                                            <p>{sucursal.notes.notes}</p>
                                                                                        </div>
                                                                                    )}
                                                                                </AccordionDetails>
                                                                            </Accordion>
                                                                        ))}
                                                                    </AccordionDetails>
                                                                </Accordion>
                                                            ))}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                    :
                                                    null
                                            ))}

                                            <div className="w-full flex justify-center mt-auto py-4">
                                                <Pagination
                                                    size="large"
                                                    color="primary"
                                                    count={paginationCount}
                                                    defaultPage={1}
                                                    page={currentPage}
                                                    siblingCount={1}
                                                    boundaryCount={2}
                                                    onChange={changePaginationHandler}
                                                />
                                            </div>
                                        </div>
                                    }

                                    <div className="col-span-3 w-full overflow-y-auto flex p-2 flex-col order-last border-l-2 border-light">
                                        <Accordion className="accordion-search-filter">
                                            <AccordionSummary
                                                expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                            >
                                                <p className="font-semibold text-mid">Fecha</p>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <div className="flex flex-col items-center gap-12">
                                                    <div className="flex flex-col gap-2">
                                                        <p className="font-semibold">Fecha de inicio</p>
                                                        <IonDatetime
                                                            presentation="date"
                                                            value={formatDateToISODateString(startDate)}
                                                            max={formatDateToISODateString(today)}
                                                            onIonChange={(e: any) => {
                                                                const selectedDate = e.detail.value ? formatISODateStringToDate(e.detail.value, false) : new Date();
                                                                setStartDate(selectedDate ?? new Date());
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="flex flex-col gap-2">
                                                        <p className="font-semibold">Fecha de fin</p>
                                                        <IonDatetime
                                                            presentation="date"
                                                            value={formatDateToISODateString(finishDate)}
                                                            max={formatDateToISODateString(new Date())}
                                                            onIonChange={(e: any) => {
                                                                const selectedDate = e.detail.value ? formatISODateStringToDate(e.detail.value, false) : new Date();
                                                                setFinishDate(selectedDate ?? new Date());
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="w-full xl:w-1/2">
                                                        <SolidButton text="Aplicar" backgroundColor="primary" disabled={false} onClickHandler={() => {
                                                            getReportData(new AbortController().signal);

                                                        }} />
                                                    </div>
                                                </div>
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion className="accordion-search-filter">
                                            <AccordionSummary
                                                expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                            >
                                                <p className="font-semibold text-mid">Displays</p>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <div className="flex flex-col">
                                                    {searchFilters.user.length === 0 ? (
                                                        <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                    ) : (
                                                        searchFilters.user.map((item: any) => (
                                                            <FormControlLabel
                                                                key={item.key}
                                                                label={item.name + " " + item.lastName}
                                                                control={
                                                                    <Checkbox
                                                                        id={item.key}
                                                                        name={item.name + " " + item.lastName}
                                                                        value={item.key}
                                                                        checked={item.checked}
                                                                        onChange={(event) => changeSearchFilterHandler(item, "user")}
                                                                    />
                                                                }
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion className="accordion-search-filter">
                                            <AccordionSummary
                                                expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                            >
                                                <p className="font-semibold text-mid">Sucursales</p>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <div className="flex flex-col">
                                                    {searchFilters.sucursal.length === 0 ? (
                                                        <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                    ) : (
                                                        searchFilters.sucursal.map((item: any) => (
                                                            <FormControlLabel
                                                                key={item.key}
                                                                label={item.name}
                                                                control={
                                                                    <Checkbox
                                                                        id={item.key}
                                                                        name={item.name}
                                                                        value={item.key}
                                                                        checked={item.checked}
                                                                        onChange={(event) => changeSearchFilterHandler(item, "sucursal")}
                                                                    />
                                                                }
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                            </AccordionDetails>
                                        </Accordion>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonPage >
    )
}
